import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import TenueProfileLayout from './TenueProfileLayout';

// import utility file
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import TenueService from '../../services/TenueService';

const ViewTenue = () => {
	const { id } = useParams();
	const [tenue, setTenue] = useState(null);
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [selectedType, setSelectedType] = useState([]);
	const [selectedTaille, setSelectedTaille] = useState('');

	useEffect(() => {
		Promise.all([TenueService.getTenue(id)]).then(([tenueData]) => {
			setTenue(tenueData.data);

			// Set default selection for taille
			if (tenueData && tenueData.data) {
				const tenueTaille = tenueData.data.taille;
				setSelectedTaille(tenueTaille);
			}

			// Set default selection for type
			if (tenueData && tenueData.data) {
				const tenueType = tenueData.data.type;
				setSelectedType(tenueType);
			}
		});
	}, [id]);

	const TYPE_CHOICES = [
		{ value: 'tenue_classe', label: 'Tenue Classe' },
		{
			value: 'tenue_sport',
			label: 'Tenue Sport'
		},
		{ value: 'pull', label: 'Pull' }
	];

	const TAILLE_CHOICES = [
		{ value: 's', label: 'S' },
		{ value: 'm', label: 'M' },
		{ value: 'l', label: 'L' },
		{
			value: 'xl',
			label: 'XL'
		},
		{ value: 'xxl', label: 'XXL' },
		{ value: '3xl', label: '3XL' }
	];

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const tenueData = new FormData();

				// Add all your form data to the FormData
				tenueData.append('type', form.type.value);
				tenueData.append('fournisseur', form.fournisseur.value);
				tenueData.append('quantite', form.quantite.value);
				tenueData.append('prix', form.prix.value);
				tenueData.append('taille', form.taille.value);

				// Call the EleveService to create the eleve
				const response = await TenueService.updateTenue(id, tenueData);

				// Reset the form and state variables after successful update
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Add this line to refresh the page
				window.location.reload();
			} catch (error) {
				console.error(
					'Erreur lors de la mise à jour des informations de la tenue',
					error
				);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<TenueProfileLayout>
			{tenue && (
				<>
					<Card className="mb-3 ">
						{/* Card body */}
						<Card.Body>
							{/*  Form  */}
							<Form
								className="mb-3"
								noValidate
								validated={validated}
								onSubmit={handleSubmit}
							>
								{showAlert && (
									<Alert variant="danger">
										<ExclamationTriangleFill size={25} className="me-1" />
										{alertMessage}
									</Alert>
								)}
								<Row>
									{/* type */}
									<Col xs={12} className="mb-3">
										<Form.Label htmlFor="type">
											Type <span className="text-danger">*</span>
										</Form.Label>
										<Form.Select
											name="type"
											required
											value={selectedType}
											isInvalid={!!feedbackMessages['type']}
											onChange={(e) => {
												const value = e.target.value;
												setSelectedType(value);
												// Reset feedback messages for points fields when type changes
												setFeedbackMessages({});
											}}
										>
											<option value="">Sélectionner le type</option>
											{TYPE_CHOICES.map((choice, index) => (
												<option key={index} value={choice.value}>
													{choice.label}
												</option>
											))}
										</Form.Select>
										<Form.Control.Feedback type="invalid">
											{feedbackMessages['type']}
										</Form.Control.Feedback>
									</Col>

									{/* taille */}
									<Col xs={12} className="mb-3">
										<Form.Label htmlFor="taille">
											Taille <span className="text-danger">*</span>
										</Form.Label>
										<Form.Select
											name="taille"
											required
											value={selectedTaille}
											isInvalid={!!feedbackMessages['taille']}
											onChange={(e) => {
												const value = e.target.value;
												setSelectedTaille(value);
												// Reset feedback messages for points fields when taille changes
												setFeedbackMessages({});
											}}
										>
											<option value="">Sélectionner la taille</option>
											{TAILLE_CHOICES.map((choice, index) => (
												<option key={index} value={choice.value}>
													{choice.label}
												</option>
											))}
										</Form.Select>
										<Form.Control.Feedback type="invalid">
											{feedbackMessages['taille']}
										</Form.Control.Feedback>
									</Col>

									<Col xs={12} className="mb-3">
										{/* prix et quantite  */}
										<Form.Group>
											<Row>
												<Col md={6} sm={12} className="mb-3">
													<Form.Label htmlFor="prix">
														Prix <span className="text-danger">*</span>
													</Form.Label>
													<Form.Control
														name="prix"
														type="text"
														placeholder="Saisir le prix de la tenue"
														id="prix"
														required
														isInvalid={!!feedbackMessages['prix']}
														onChange={(e) => {
															setFeedbackMessages((prevMessages) => ({
																...prevMessages,
																prix: e.target.value
																	? ''
																	: 'Le prix est requis.'
															}));
														}}
														defaultValue={tenue.prix}
													/>
													<Form.Control.Feedback type="invalid">
														{feedbackMessages['prix']}
													</Form.Control.Feedback>
												</Col>
												<Col md={6} sm={12} className="mb-3">
													<Form.Label htmlFor="quantite">
														Quantité <span className="text-danger">*</span>
													</Form.Label>
													<Form.Control
														name="quantite"
														type="text"
														placeholder="Saisir le quantité de tenues"
														id="quantite"
														required
														isInvalid={!!feedbackMessages['quantite']}
														onChange={(e) => {
															setFeedbackMessages((prevMessages) => ({
																...prevMessages,
																quantite: e.target.value
																	? ''
																	: 'La quantité de tenues est requise.'
															}));
														}}
														defaultValue={tenue.quantite}
													/>
													<Form.Control.Feedback type="invalid">
														{feedbackMessages['quantite']}
													</Form.Control.Feedback>
												</Col>
											</Row>
										</Form.Group>
									</Col>

									<Col xs={12} className="mb-3">
										{/* fournisseur  */}
										<Form.Group>
											<Form.Label htmlFor="fournisseur">
												Fournisseur <span className="text-danger">*</span>
											</Form.Label>
											<Form.Control
												name="fournisseur"
												type="text"
												placeholder="Saisir le fournisseur de tenues"
												id="fournisseur"
												required
												isInvalid={!!feedbackMessages['fournisseur']}
												onChange={(e) => {
													setFeedbackMessages((prevMessages) => ({
														...prevMessages,
														fournisseur: e.target.value
															? ''
															: 'Le fournisseur de tenues est requise.'
													}));
												}}
												defaultValue={tenue.fournisseur}
											/>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['fournisseur']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									{/* Buttons */}
									<Col xs={12} className="mt-4">
										<Button variant="success" type="submit">
											Mettre à jour
										</Button>
										<Button
											type="reset"
											variant="outline-danger"
											className="ms-2"
										>
											Effacer
										</Button>
									</Col>
								</Row>
							</Form>
						</Card.Body>
					</Card>
				</>
			)}
		</TenueProfileLayout>
	);
};

export default ViewTenue;
